import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Box,
  Typography,
  IconButton} from '@mui/material';
import { Add, Check, Close, DeleteOutlineOutlined } from '@mui/icons-material';
import { FieldType, Schema } from '../../toolympus/hooks/useSchema';
import { FormControlsForFields } from '../../toolympus/components/schemed';
import { Buttons, FormGrid, LoadingIndicator, OccupyFreeSpace, SimpleDialog } from '../../toolympus/components/primitives';
import { ThemeSettings } from '../../theme';
import { useProjectStatuses } from './useProjectStatuses';
import { statusDateLabel } from '../Settings/useAppSettings';

interface Props {
  projectId: number;
  schema: Schema;
  mode: "project" | "process";
}

export const ProjectStatusesTable = (props: Props) => {
  const { projectId } = props;
  const data = useProjectStatuses(props.mode, projectId);

  const schema = useMemo(() => {
    return {
      ...props.schema,
      status_date: { label: "Дата", type: FieldType.date },
    }
  }, [props.schema]);

  return (<>
    <Box sx={{ marginTop: 0 }}>
      <Buttons>
        {data.isLoading && <LoadingIndicator />}

        <OccupyFreeSpace />

        <Button
          startIcon={<Add />}
          size="small"
          color="primary"
          onClick={() => data.newRecord.startEditing()}
        >
          добавить
        </Button>
      </Buttons>

      {data.data.map((status) => (data.editRecord.item?._id === status._id
        ? (
            <ProjectStatusEditWrapper key={status._id}>
              <FormControlsForFields
                data={data.editRecord.item}
                onChange={(o,c) => data.editRecord.update(c)}
                schema={schema}
                fields={[
                  ["status_date", { className: "date", controlProps: { autoFocus: true }}],
                  ["description", { className: "title" }],
                ]}
                />
              <Buttons className="actions">
                  <IconButton size="small" onClick={() => data.editRecord.item && data.removeRecord.run(data.editRecord.item)}>
                    <DeleteOutlineOutlined />
                  </IconButton>
                  <IconButton size="small" onClick={() => data.editRecord.save()}>
                    <Check />
                  </IconButton>
                  <IconButton size="small" onClick={() => data.editRecord.cancel()}>
                    <Close />
                  </IconButton>
              </Buttons>
          </ProjectStatusEditWrapper>)
          : (
            <ProjectStatusWrapper key={status._id} onClick={() => !data.editRecord.item && data.editRecord.startEditing({ ...status })}>
              <Typography className="date" variant="caption" component="p" color="textSecondary">{statusDateLabel(status.status_date)}</Typography>
              <Typography className="description">{status.description}</Typography>
            </ProjectStatusWrapper>
            )))}
    </Box>

    <SimpleDialog
      dialogTitle="Добавить задачи"
      submitOnModEnter
      noFullscreen
      save={() => data.newRecord.save()}
      {...data.newRecord.dialog}>
        <FormGrid columns="1fr" noMargin>
          {!!data.newRecord.item &&
            <FormControlsForFields
              data={data.newRecord.item}
              onChange={(o,c) => data.newRecord.update(c)}
              schema={schema}
              fields={[
                ["status_date", { controlProps: { autoFocus: true }, autoComplete: true }],
                ["description"],
              ]}
              />}

        </FormGrid>
    </SimpleDialog>
  </>);
};

const ProjectStatusWrapper = styled(FormGrid)`
  grid-template-columns: 1fr;
  border-bottom: 1px solid ${ThemeSettings.colors.border_light};

  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  margin: 0;

  & .description {
    white-space: pre-line;
  }
`;

const ProjectStatusEditWrapper = styled(ProjectStatusWrapper)`
  background: ${ThemeSettings.colors.background_panel};
  border-radius: 12px;
  padding: 0.5rem;
  border-bottom: none;
  margin: 0.5rem 0;

  & .actions {
    justify-content: flex-end;
  }
`;